// src/components/Visualization/ComparisonChart.js
import React, { useState } from 'react';
import { 
  ResponsiveContainer, 
  ComposedChart, 
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ReferenceLine,
  Label
} from 'recharts';
import { BarChart2, ArrowRight, ChevronDown, Filter } from 'lucide-react';

/**
 * ComparisonChart - Gráfico de comparação avançado entre múltiplos itens
 * 
 * @param {Object} props
 * @param {Array} props.data - Dados para comparação
 * @param {string} props.nameKey - Chave para os nomes dos itens (eixo X ou categorias)
 * @param {Array} props.metrics - Configuração das métricas a exibir
 * @param {string} props.title - Título do gráfico
 * @param {string} props.subtitle - Subtítulo do gráfico
 * @param {boolean} props.showControls - Exibir controles de configuração do gráfico
 * @param {Array} props.filters - Filtros disponíveis para os dados
 * @param {Array} props.colorGroups - Grupos de cores para categorias específicas
 * @param {Array} props.referenceLines - Linhas de referência a exibir (ex: média, meta)
 * @param {Array} props.limitItems - Limitar número de itens a exibir
 * @param {boolean} props.stacked - Exibir gráfico empilhado
 * @param {string} props.layout - Layout do gráfico ('vertical' ou 'horizontal')
 * @param {number} props.height - Altura do gráfico
 */
const ComparisonChart = ({
  data = [],
  nameKey = 'name',
  metrics = [],
  title = 'Comparação',
  subtitle = '',
  showControls = true,
  filters = [],
  colorGroups = [],
  referenceLines = [],
  limitItems = 0,
  stacked = false,
  layout = 'vertical',
  height = 400
}) => {
  // Estados para configurações do gráfico
  const [activeMetrics, setActiveMetrics] = useState(metrics.map(m => m.key));
  const [sortBy, setSortBy] = useState(metrics[0]?.key || '');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedSettings, setExpandedSettings] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Processar e ordenar dados
  const processedData = React.useMemo(() => {
    if (!data.length) return [];
    
    // Aplicar filtros
    let filteredData = [...data];
    if (activeFilter !== 'all') {
      const filter = filters.find(f => f.value === activeFilter);
      if (filter && filter.filterFn) {
        filteredData = filteredData.filter(filter.filterFn);
      }
    }
    
    // Ordenar dados
    if (sortBy) {
      filteredData.sort((a, b) => {
        const valueA = a[sortBy] || 0;
        const valueB = b[sortBy] || 0;
        return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
      });
    }
    
    // Limitar número de itens
    if (limitItems > 0 && filteredData.length > limitItems) {
      filteredData = filteredData.slice(0, limitItems);
    }
    
    return filteredData;
  }, [data, activeFilter, filters, sortBy, sortDirection, limitItems]);
  
  // Alternar seleção de métrica
  const toggleMetric = (metricKey) => {
    setActiveMetrics(prev => {
      // Se já está selecionada, remover
      if (prev.includes(metricKey)) {
        // Não permitir remover todas as métricas
        if (prev.length === 1) return prev;
        return prev.filter(key => key !== metricKey);
      } 
      // Caso contrário, adicionar
      return [...prev, metricKey];
    });
  };
  
  // Alternar ordenação
  const toggleSort = (metricKey) => {
    if (sortBy === metricKey) {
      // Inverter direção se a métrica já estiver selecionada
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Nova métrica para ordenação, começar com descendente
      setSortBy(metricKey);
      setSortDirection('desc');
    }
  };
  
  // Verificar se é um layout horizontal
  const isHorizontal = layout === 'horizontal';
  
  // Customizar tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Encontrar o item completo nos dados
      const item = processedData.find(d => d[nameKey] === label);
      
      return (
        <div className="bg-white p-3 shadow-lg border border-gray-200 rounded-lg">
          <h3 className="font-medium text-gray-800 mb-2">{label}</h3>
          <div className="space-y-1">
            {payload.map((entry, index) => {
              const metric = metrics.find(m => m.key === entry.dataKey);
              
              return (
                <div key={`tooltip-${index}`} className="flex justify-between items-center">
                  <div className="flex items-center">
                    <div 
                      className="h-3 w-3 rounded-full mr-2"
                      style={{ backgroundColor: entry.color }}
                    />
                    <span className="text-xs text-gray-600">
                      {metric?.label || entry.dataKey}:
                    </span>
                  </div>
                  <span className="text-xs font-semibold ml-4">
                    {metric?.valueFormatter 
                      ? metric.valueFormatter(entry.value, item)
                      : entry.value
                    }
                    {metric?.unit && ` ${metric.unit}`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    
    return null;
  };
  
  // Renderizar área de controles
  const renderControls = () => {
    if (!showControls) return null;
    
    return (
      <>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <BarChart2 size={20} className="text-blue-600 mr-2" />
            <div>
              <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
              {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Filtros */}
            {filters.length > 0 && (
              <div className="relative">
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  <span className="pl-2 pr-1 text-gray-500">
                    <Filter size={16} />
                  </span>
                  <select
                    className="bg-transparent border-none text-sm focus:ring-0 py-1"
                    value={activeFilter}
                    onChange={(e) => setActiveFilter(e.target.value)}
                  >
                    <option value="all">Todos</option>
                    {filters.map((filter, idx) => (
                      <option key={`filter-${idx}`} value={filter.value}>
                        {filter.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            
            {/* Expandir/colapsar configurações */}
            <button
              className={`p-1.5 rounded-lg ${
                expandedSettings ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              onClick={() => setExpandedSettings(!expandedSettings)}
            >
              <ChevronDown 
                size={18} 
                className={`transform transition-transform ${expandedSettings ? 'rotate-180' : ''}`} 
              />
            </button>
          </div>
        </div>
        
        {/* Configurações expandidas */}
        {expandedSettings && (
          <div className="p-3 bg-gray-50 rounded-lg mb-4 flex flex-wrap items-center gap-3">
            <div className="text-sm font-medium text-gray-600 mr-2">Métricas:</div>
            
            {metrics.map((metric, idx) => (
              <button
                key={`metric-${idx}`}
                className={`px-3 py-1.5 text-xs rounded-full ${
                  activeMetrics.includes(metric.key)
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                onClick={() => toggleMetric(metric.key)}
              >
                {metric.label}
              </button>
            ))}
            
            <div className="border-l border-gray-300 h-6 mx-2" />
            
            <div className="text-sm font-medium text-gray-600 mr-2">Ordenar por:</div>
            
            {metrics.map((metric, idx) => (
              <button
                key={`sort-${idx}`}
                className={`px-3 py-1.5 text-xs rounded-full flex items-center ${
                  sortBy === metric.key
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                onClick={() => toggleSort(metric.key)}
              >
                {metric.label}
                {sortBy === metric.key && (
                  <ArrowRight 
                    size={14} 
                    className={`ml-1 ${sortDirection === 'desc' ? 'rotate-90' : '-rotate-90'} transform`} 
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </>
    );
  };
  
  // Se não tiver dados, mostrar mensagem
  if (!processedData.length) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-500">Sem dados disponíveis para exibição.</p>
      </div>
    );
  }
  
  return (
    <div className="w-full">
      {renderControls()}
      
      <div style={{ height, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={processedData}
            layout={isHorizontal ? 'horizontal' : 'vertical'}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            
            {isHorizontal ? (
              <>
                <XAxis 
                  dataKey={nameKey} 
                  scale="point" 
                  padding={{ left: 20, right: 20 }} 
                />
                <YAxis />
              </>
            ) : (
              <>
                <XAxis type="number" />
                <YAxis 
                  dataKey={nameKey} 
                  type="category" 
                  scale="band" 
                  width={150}
                />
              </>
            )}
            
            <Tooltip content={<CustomTooltip />} />
            
            {showControls && (
              <Legend 
                wrapperStyle={{ paddingTop: 10 }}
                onClick={(entry) => toggleMetric(entry.dataKey)}
              />
            )}
            
            {/* Renderizar métricas ativas */}
            {metrics
              .filter(metric => activeMetrics.includes(metric.key))
              .map((metric, index) => {
                // Determinar o tipo de visualização
                const ChartComponent = metric.chartType === 'line' ? Line : Bar;
                
                return (
                  <ChartComponent
                    key={`chart-${metric.key}`}
                    type="monotone"
                    dataKey={metric.key}
                    name={metric.label}
                    fill={metric.color}
                    stroke={metric.chartType === 'line' ? metric.color : undefined}
                    yAxisId={metric.yAxisId || "left"}
                    stackId={stacked && metric.chartType !== 'line' ? 'stack1' : undefined}
                  />
                );
              })
            }
            
            {/* Linhas de referência */}
            {referenceLines.map((line, index) => (
              <ReferenceLine
                key={`ref-line-${index}`}
                {...(isHorizontal ? { y: line.value } : { x: line.value })}
                stroke={line.color || "#ff7300"}
                strokeDasharray="3 3"
                strokeWidth={2}
              >
                {line.label && (
                  <Label
                    value={line.label}
                    position={isHorizontal ? "insideBottomRight" : "insideTopRight"}
                    fill={line.color || "#ff7300"}
                  />
                )}
              </ReferenceLine>
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ComparisonChart;
